import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { BehaviorSubject, Observable, Subject, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

import { ImageViewerComponent, ImageViewerData } from '../../components/image-viewer/image-viewer.component';
import { CuttingButt } from '../../models/cutting/cutting-butt.model';
import { CuttingHandle } from '../../models/cutting/cutting-handle.model';
import { CuttingMatBaseGroup } from '../../models/cutting/cutting-material-group.model';
import { ApiLinks } from '../cutting-panel-list/csl.api-links';
import { HandlesDialogService, HandlesResponse } from './handles-dialog.service';

export interface MaterialBaseHandlesDialogData {
  dataProvider?: HandlesDialogService;
}

@Component({
  templateUrl: './handles-dialog.component.html',
  styleUrls: ['./handles-dialog.component.scss'],
})
export class HandlesDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource: MatTableDataSource<CuttingHandle> = new MatTableDataSource();
  displayedColumns = ['select', 'texture', 'name', 'article'];
  loadMore$: BehaviorSubject<[number, number]> = new BehaviorSubject([0, 25]);
  loading = false;

  imageViewerDialogRef: MatDialogRef<ImageViewerComponent>;

  @ViewChild('filter', { static: true }) filter: ElementRef;
  @ViewChild('filterButton', { static: true }) filterButton: MatButton;

  pathToTextures = '';
  buttsTotalCount = 0;
  selectedButt: CuttingButt = undefined;
  groups: CuttingMatBaseGroup[] = [];
  currentGroupId = 0;
  focusedGroupId = -1;

  unsubscribe$: Subject<void> = new Subject();

  handles$: Observable<HandlesResponse>;
  groups$: Observable<CuttingMatBaseGroup[]>;

  dataProvider: HandlesDialogService;

  onSelectGroup(group: CuttingMatBaseGroup): void {
    this.currentGroupId = group.id;
    this.dataSource.data = [];
    this.loadMore$.next([0, 25]);
  }

  get hasMore(): boolean {
    return this.buttsTotalCount - this.dataSource.data.length > 0;
  }

  constructor(
    public dialogRef: MatDialogRef<HandlesDialogComponent>,
    public buttFormService: HandlesDialogService,
    public dialog: MatDialog,

    @Inject(MAT_DIALOG_DATA) public data: MaterialBaseHandlesDialogData
  ) {
    this.pathToTextures = ApiLinks.materialTexture;

    this.dataProvider = this.data.dataProvider || this.buttFormService;
  }

  ngOnInit(): void {
    this.getGroups();
  }

  ngAfterViewInit(): void {
    this.addListners();
  }

  showFullNameTooltip(el: HTMLElement): boolean {
    if (el.offsetWidth < el.scrollWidth) {
      return true;
    } else {
      return false;
    }
  }

  private addListners(): void {
    const filterEvent = merge(
      fromEvent(this.filter.nativeElement, 'keyup'), //
      fromEvent(this.filterButton._elementRef.nativeElement, 'click')
    ).pipe(debounceTime(500), distinctUntilChanged());

    this.loadMore$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.getHandles();
    });

    filterEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.dataSource.data = [];
      this.loadMore$.next([0, 25]);
    });
  }

  private getHandles(): void {
    this.loading = true;
    this.filter.nativeElement.value = (this.filter.nativeElement.value as string).replace(/'/g, '');

    this.handles$ = this.dataProvider
      .getHandles(
        this.currentGroupId,
        this.loadMore$.value[1],
        this.loadMore$.value[0],
        this.filter.nativeElement.value
      )
      .pipe(
        tap(response => {
          this.loading = false;

          this.dataSource.data.push(...response.materialsDTO);
          this.dataSource.data = this.dataSource.data;

          this.buttsTotalCount = response.materialsCount;
        })
      );
  }

  getGroups(): void {
    this.groups$ = this.dataProvider.getHandleGroupsFromMatBase().pipe(
      tap(response => {
        this.groups = response;

        if (response.length > 0) {
          this.focusedGroupId = response[0].id;
        }
      })
    );
  }

  setImagePlug(e: Event): void {
    (e.target as HTMLImageElement).src = '';
  }

  toggleSelection(item: CuttingButt): void {
    if (this.selectedButt === item) {
      this.selectedButt = undefined;
    } else {
      this.selectedButt = item;
    }
  }

  saveAndClose(): void {
    this.dialogRef.close(this.selectedButt);
  }

  isSelected(item: CuttingButt): boolean {
    if (!this.selectedButt) {
      return false;
    }

    return (
      this.selectedButt.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
      this.selectedButt.sign.toLowerCase().trim() === item.sign.toLowerCase().trim() &&
      this.selectedButt.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );
  }

  openTextureViewerDialog(link: string): void {
    this.imageViewerDialogRef = this.dialog.open(ImageViewerComponent, {
      panelClass: 'image-viewer-dialog',
      disableClose: false,
      data: {
        textureLink: link,
      } as ImageViewerData,
    });

    this.imageViewerDialogRef.afterClosed().subscribe(() => {
      this.imageViewerDialogRef = null;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
